import React, { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";

// Language label normalizer (optional)
const LANG_LABELS = { js:"JavaScript", javascript:"JavaScript", ts:"TypeScript", typescript:"TypeScript",
  jsx:"JSX", tsx:"TSX", py:"Python", python:"Python", cs:"C#", csharp:"C#", "c#":"C#",
  html:"HTML", css:"CSS", json:"JSON", bash:"Bash", sh:"Bash", shell:"Bash", sql:"SQL",
  cpp:"C++", "c++":"C++", c:"C", java:"Java", go:"Go", rust:"Rust", php:"PHP", ruby:"Ruby", yml:"YAML", yaml:"YAML", r:"R"
};
function extractLang(className = "") {
  const token = className.split(/\s+/).find(c => c.startsWith("language-"));
  const raw = token ? token.slice(9) : "";
  const clean = raw.replace(/[^A-Za-z0-9#+-]/g, "").toLowerCase();
  return LANG_LABELS[clean] || (clean || "text");
}

function htmlToNewlines(s) {
  return s
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p\s*>/gi, "\n\n")
    .replace(/<p[^>]*>/gi, "");
}

function normaliseForMarkdown(s) {
  return s
    // 1) turn literal escape sequences into real chars
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    // 2) unescape triple backticks if they came through as \`\`\`
    .replace(/\\`\\`\\`/g, "```")
    // 3) remove zero-widths / BOM / NBSP
    .replace(/[\u200B-\u200D\uFEFF\u200E\u200F]/g, "")
    .replace(/\u00A0/g, " ")
    // 4) ensure a blank line before any ``` that starts mid-line
    .replace(/([^\n])\n```/g, "$1\n\n```");
}


/* ---------- NEW: Azure "-----" snippet fencing ---------- */

// Split on any fenced block (```...```, ````...````, ~~~...~~~)
const SPLIT_FENCES = /(`{3,}[\s\S]*?`{3,}|~{3,}[\s\S]*?~{3,})/g;

// Wrap text between lines of ≥8 hyphens with ``` fences (outside existing fences)
function fenceAzureSnippets(md) {
  const parts = md.replace(/\r\n?/g, "\n").split(SPLIT_FENCES);
  for (let i = 0; i < parts.length; i += 2) {
    // only operate on non-fenced chunks (even indexes)
    parts[i] = parts[i].replace(
      /(^|\n)\s*-{8,}\s*\n([\s\S]*?)\n\s*-{8,}\s*(?=\n|$)/g,
      (_m, lead, body) => {
        const lang = ""; // keep empty to avoid guessing
        const clean = body.replace(/^\n+|\n+$/g, "");
        return `${lead}\n\`\`\`${lang}\n${clean}\n\`\`\`\n`;
      }
    );
  }
  return parts.join("");
}

/* -------------------------------------------------------- */

// Inline code (keep it inline, GPT-style pill)
function InlineCode({ children, className, node, inline, ...rest }) {
  const { title } = rest; // pick allowed attrs only
  return (
    <code
      title={title}
      className={
        `inline align-baseline rounded-md px-1.5 py-0.5
         border border-neutral-800 bg-neutral-900/60 ` + (className || "")
    }
    >
      {children}
    </code>
  );
}

// Block code: replace the entire <pre> node (so it never sits inside <p>)
function CodeBlock({ children }) {
  // children[0] is the <code> element react-markdown would have rendered
  const codeEl = Array.isArray(children) ? children[0] : children;
  const className = codeEl?.props?.className || "";
  const codeText = String(codeEl?.props?.children || "").replace(/\n$/, "");
  const lang = extractLang(className);

  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(codeText);
    setCopied(true); setTimeout(() => setCopied(false), 900);
  };

  return (
    <div className="not-prose my-3 overflow-hidden rounded-xl border border-neutral-800">
      <div className="flex items-center justify-between bg-neutral-900/70 px-3 py-1.5 border-b border-neutral-800">
        <span className="text-sm leading-5 text-neutral-400">{lang}</span>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1.5 h-6 px-3 text-sm leading-5
                     rounded-md border border-transparent text-neutral-300 hover:text-white
                     hover:bg-neutral-800/40 focus:outline-none
                     focus:ring-1 focus:ring-neutral-700"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="m-0 overflow-x-auto bg-[#0b1220] p-4 text-sm">
        <code className={className}>{codeText}</code>
      </pre>
    </div>
  );
}


/** GPT-like Markdown renderer with Tailwind prose + Copy button on code blocks */
export default function AssistantMarkdown({ text, allowHtml = false }) {
  // console.log(`text to render:`, text);
  const processed = React.useMemo(
    () =>
      fenceAzureSnippets(
        normaliseForMarkdown(
          htmlToNewlines(text)
        )
      ),
    [text]
  );

const H = (Tag, cls) => (props) => (
  <Tag {...props} className={[cls, props.className].filter(Boolean).join(" ")} />
);


  return (
    
    <div className="prose dark:prose-invert max-w-prose prose-pre:m-0 prose-pre:p-0 prose-code:before:content-[''] prose-code:after:content-[''] prose-h2:text-xl prose-h3:text-lg leading-[1.75]">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={allowHtml ? [rehypeRaw, rehypeSanitize] : [rehypeSanitize]}
        components={{
          pre: CodeBlock,     // block code handled here (no <pre> inside <p>)
          code: InlineCode,   // inline code stays as <code> (safe inside <p>)
          hr: (props) => (
            <hr
              {...props}
              className="my-4 border-t"
            />
          ),
            // Ordered lists
            ol: (props) => (
            <ol
    {...props}
    className={
      "my-4 space-y-2 pl-0 list-none " +
      "[counter-reset:item] " +
      // each li becomes a container with left padding for the number
      "[&>li]:relative [&>li]:pl-10 [&>li]:[counter-increment:item] " +
      // custom number, vertically centred
      "[&>li::before]:content-[counter(item)'.'] " +
      "[&>li::before]:absolute [&>li::before]:left-4 " +
      "[&>li::before]:top-2 [&>li::before]:translate-y-0" +
      "[&>li::before]:w-8 [&>li::before]:text-right " +
      "[&>li::before]:font-semibold [&>li::before]:text-neutral-400 "
    }
  />
            ),

            p: (props) => (
              <p
                {...props}
                className="block w-full pt-2 pb-4 px-2"
              />
            ),
            // Unordered lists
            ul: (props) => (
            <ul
                {...props}
                className={
                "my-4 pl-8 list-disc space-y-1 " +
                "[&>li::marker]:font-semibold " +
                "[&>li]:leading-7 [&>li]:pl-1.5"
                }
            />
            ),
            h1: H("h1", "my-3 text-3xl md:text-4xl font-semibold tracking-tight"),
            h2: H("h2", "my-2 text-2xl md:text-3xl font-semibold tracking-tight"),
            h3: H("h3", "my-2 text-xl  md:text-2xl font-semibold"),
            h4: H("h4", "my-2 text-lg  md:text-xl  font-semibold"),
            h5: H("h5", "my-1 text-base font-semibold"),
            h6: H("h6", "my-1 text-sm  font-semibold uppercase"),
        }}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
}
